import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { getTargetValue } from "../../../shared/connectors/getTargetValue";
import type { TestUrlField as TestUrlFieldType } from "../../../shared/connectors/types";
import { sharedMutators } from "../../../shared/mutators";
import { Box } from "../Box";
import { Input } from "../Input";
import { SegmentedControl } from "../SegmentedControl";
import { Text } from "../Text";
import type { App } from "./Form";
import { useRenderWithPlaceholders } from "./renderWithPlaceholders";

const Row = styled("div")`
  display: flex;
  gap: 8px;
  align-items: center;
`;

type Props = {
  item: TestUrlFieldType;
  app: App;
  disabled?: boolean;
  onEdit?: () => void;
};

export function TestUrlField({ item, app, disabled, onEdit }: Props) {
  const z = useZero();
  const renderWithPlaceholders = useRenderWithPlaceholders();
  const url = getTargetValue(item.urlTarget, app) as string | undefined;
  const method = (getTargetValue(item.methodTarget, app) as string) || "GET";

  const persist = (field: "url" | "method", value: string) => {
    const updated = {
      ...app.configuration,
      [item.urlTarget.field]: field === "url" ? value : (url ?? ""),
      [item.methodTarget.field]: field === "method" ? value : method,
    };
    z.mutate(
      sharedMutators.updateConnectedApp({
        connectedAppId: app.id,
        name: app.name,
        identifier: app.identifier,
        urlPattern: app.urlPattern,
        configuration: updated,
      })
    );
  };

  return (
    <div>
      <Box horizontal={8} vertical={10}>
        <Text variant="text-small-md" color="var(--text-primary)">
          {item.label}
        </Text>
      </Box>
      <Row>
        <SegmentedControl
          options={["GET", "POST"] as const}
          value={method as "GET" | "POST"}
          onChange={(m) => {
            onEdit?.();
            persist("method", m);
          }}
          disabled={disabled}
        />
        <Input
          type="text"
          defaultValue={url}
          placeholder={item.placeholder}
          onFinishChange={(value) => persist("url", value)}
          style={{ flex: 1, minWidth: 0 }}
          readOnly={disabled}
        />
      </Row>
      {item.helperText && (
        <Box horizontal={8} top={6}>
          <Text variant="text-small" color="var(--text-secondary)">
            {renderWithPlaceholders(item.helperText)}
          </Text>
        </Box>
      )}
    </div>
  );
}
